import { Suspense, lazy, useEffect, useMemo, useRef } from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import {
  fetchWebsiteTemplateBootstrap,
  markCreatorPublicHomeIntent,
  prefetchWebsiteTemplateBootstrap,
  registerCreatorPublicHomePreload,
  websiteTemplateBootstrapQueryKey,
} from "../DashboardView/MasterTemplateEditor/websiteTemplateBootstrap";
import { queryClient } from "../../Utils/query/queryClient";
import { supabase } from "../../config/supabaseClient";
import { getStayUpToDateComponents } from "../../Utils/HelperFunction/HelperFunction";
import { prefetchProfileSetup } from "../../Utils/customHooks/useLoadProfileSetup";
import { useQuery } from "../../Utils/query";
import {
  readCachedCreatorPublicSurface,
  writeCachedCreatorPublicSurface,
  type CreatorPublicSurface,
} from "../../Utils/url/creatorPublicSurface";
import {
  creatorPublicPathOmitsUsernamePrefix,
  isKnownWebsiteTemplateRoute,
} from "../../Utils/url/getCreatorSpaRelativePath";
import { isCreatorPagePublicPathname } from "../../Utils/CustomDomain/CustomDomainFuntions";

const loadFollowerViewNew = () => import("../FollowerViewNew");
const loadWebsiteTemplatePage = () =>
  import("../WebsiteTemplatePage/WebsiteTemplatePage");

const FollowerViewNew = lazy(loadFollowerViewNew);
const WebsiteTemplatePage = lazy(loadWebsiteTemplatePage);
const CreatorPagesView = lazy(
  () => import("../../features/creator-pages/CreatorPagesView"),
);

const preloadedSurfaces = new Set<string>();

/** Same `import()` as the lazy routes, so the chunk download overlaps bootstrap. */
export function preloadCreatorPublicSurface(
  username: string,
  surface: CreatorPublicSurface,
): void {
  const u = username.trim().toLowerCase();
  if (!u) return;
  const id = `${surface}:${u}`;
  if (preloadedSurfaces.has(id)) return;
  preloadedSurfaces.add(id);
  if (surface === "website") {
    void loadWebsiteTemplatePage();
    return;
  }
  void loadFollowerViewNew();
  prefetchProfileSetup(u);
  void queryClient.prefetchQuery(
    ["landing-stayuptodate", u, "landing-page"],
    () => getStayUpToDateComponents(u, supabase),
    { staleTime: 5 * 60 * 1000 },
  );
}

registerCreatorPublicHomePreload(preloadCreatorPublicSurface);

function normalizeUsername(raw: string | undefined): string {
  const trimmed = raw?.trim();
  if (!trimmed) return "";
  try {
    return decodeURIComponent(trimmed).trim().toLowerCase();
  } catch {
    return trimmed.toLowerCase();
  }
}

/** Public interim loader — blank until the page chunk is ready. */
function PublicBlankLoader() {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label="Loading"
      style={{ minHeight: "100dvh", background: "#fff" }}
    />
  );
}

function WebsiteUsernameLoader({ username: _username }: { username: string }) {
  return <PublicBlankLoader />;
}

function LibUsernameLoader({ username: _username }: { username: string }) {
  return <PublicBlankLoader />;
}

function useCreatorPublicSurface(
  username: string,
  isHome: boolean,
  isWebsitePath: boolean,
): {
  surface: CreatorPublicSurface | null;
} {
  const lastPrefetchedRef = useRef<string | undefined>(undefined);
  const cachedSurface = username
    ? readCachedCreatorPublicSurface(username)
    : null;

  // Always warm bootstrap so website theme can paint into the loader quickly.
  if (username && username !== lastPrefetchedRef.current) {
    lastPrefetchedRef.current = username;
    if (isHome) markCreatorPublicHomeIntent(username);
    // Known website paths (home, shop, search, content, about, product) start
    // that file now. A known link-in-bio visit loads its own file, and an
    // unknown link slug does not download the website editor.
    if (cachedSurface === "link-in-bio") {
      if (isHome) preloadCreatorPublicSurface(username, "link-in-bio");
    } else if (isHome || isWebsitePath || cachedSurface === "website") {
      preloadCreatorPublicSurface(username, "website");
    }
    prefetchWebsiteTemplateBootstrap(username);
  }

  const bootstrapQuery = useQuery(
    websiteTemplateBootstrapQueryKey(username),
    ({ signal }) => fetchWebsiteTemplateBootstrap(username, signal),
    {
      enabled: Boolean(username),
      // Align with prefetch staleTime so surface detect shares one flight.
      staleTime: 30_000,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  );

  const bootstrap = bootstrapQuery.data;
  const resolvedSurface: CreatorPublicSurface | null =
    bootstrap && !bootstrap.userNotFound
      ? bootstrap.hasWebsiteSurface
        ? "website"
        : "link-in-bio"
      : bootstrap?.userNotFound || bootstrapQuery.isError
        ? "link-in-bio"
        : null;

  // Sync write so nav hrefs / guards see the live surface before the next paint.
  if (username && resolvedSurface) {
    writeCachedCreatorPublicSurface(username, resolvedSurface);
  }

  useEffect(() => {
    if (!username || !resolvedSurface) return;
    writeCachedCreatorPublicSurface(username, resolvedSurface);
  }, [username, resolvedSurface]);

  // Prefer live resolution; fall back to session cache for instant correct loader.
  return {
    surface: resolvedSurface ?? cachedSurface,
  };
}

/** Path after `/{username}/` for public website pages ("" = home). */
function useCanonicalWebsitePathRest(username: string): string {
  const params = useParams<{ pageSlug?: string; "*"?: string }>();
  const { pathname } = useLocation();

  return useMemo(() => {
    const u = String(username || "").trim().toLowerCase();
    const raw = String(pathname || "");

    // Trust the address bar first. `pageSlug` can still be "search" for a
    // frame after leaving /search, which made the home URL render Search.
    if (u) {
      for (const prefix of [`/${u}`, `/${encodeURIComponent(u)}`]) {
        if (raw === prefix || raw === `${prefix}/`) return "";
        if (raw.startsWith(`${prefix}/`)) {
          return raw.slice(prefix.length + 1).replace(/\/+$/, "");
        }
      }
    }
    if (creatorPublicPathOmitsUsernamePrefix()) {
      return raw.replace(/^\/+/, "").replace(/\/+$/, "");
    }

    const slug = String(params.pageSlug || "").trim();
    const star =
      typeof params["*"] === "string" ? params["*"].replace(/^\/+/, "") : "";
    if (slug) return star ? `${slug}/${star}` : slug;
    return "";
  }, [params.pageSlug, params["*"], pathname, username]);
}

/**
 * Layout for `/{username}` + nested paths.
 *
 * Website creators keep one `WebsiteTemplatePage` mounted while navigating
 * home → shop/content/search/link-in-bio (path rest updates in place).
 * LiB creators render child routes via `<Outlet />` only after surface is
 * confirmed, so website creators never flash FrontendSkeleton on `/{username}`.
 */
export function CreatorPublicLayout() {
  const { username: rawParam } = useParams<{ username?: string }>();
  const username = normalizeUsername(rawParam);
  const pathRest = useCanonicalWebsitePathRest(username);
  const firstSeg = pathRest.split("/").filter(Boolean)[0] || "";
  const looksLikeWebsitePath = isKnownWebsiteTemplateRoute(firstSeg);
  const isWebsiteLinkInBioPath =
    firstSeg === "link-in-bio" || pathRest.startsWith("link-in-bio/");
  const { surface } = useCreatorPublicSurface(
    username,
    pathRest === "",
    looksLikeWebsitePath || isWebsiteLinkInBioPath,
  );
  const { pathname } = useLocation();
  const isCreatorPagePath = isCreatorPagePublicPathname(pathname);

  if (!username) {
    return <LibUsernameLoader username="" />;
  }

  // `/page/:slug` is the old multipage surface — never swallow it into the website home.
  if (isCreatorPagePath) {
    return <Outlet />;
  }

  // Copied "Link in Bio" URLs are `/{user}/link-in-bio`. That must never hit
  // CreatorPagesView (`:pageSlug`), which 404s as "No user found".
  if (isWebsiteLinkInBioPath) {
    if (surface === "website") {
      return (
        <Suspense fallback={<WebsiteUsernameLoader username={username} />}>
          <WebsiteTemplatePage
            usernameOverride={username}
            forcePublicWebsite
            publicWebsitePathRest={pathRest}
          />
        </Suspense>
      );
    }
    if (surface === "link-in-bio") {
      return (
        <Navigate
          to={`/website/${encodeURIComponent(username)}/${pathRest}`}
          replace
        />
      );
    }
    return <WebsiteUsernameLoader username={username} />;
  }

  if (surface === "website") {
    // Website owns TemplatePublicLoading end-to-end (chunk + bootstrap).
    return (
      <Suspense fallback={<WebsiteUsernameLoader username={username} />}>
        <WebsiteTemplatePage
          usernameOverride={username}
          forcePublicWebsite
          publicWebsitePathRest={pathRest}
        />
      </Suspense>
    );
  }

  // Website-looking URL still resolving (or unknown) — don't mount classic LiB.
  // Once surface is confirmed link-in-bio, always Outlet (carousel slugs may
  // collide with website segment names like "shop" / "search").
  if (looksLikeWebsitePath && surface !== "link-in-bio") {
    return <WebsiteUsernameLoader username={username} />;
  }

  if (surface === "link-in-bio") {
    return <Outlet />;
  }

  // Bare `/{username}` or `/{username}/{id}` still resolving Website vs LiB.
  // Do not mount the link-in-bio child here — a fresh visit was painting that
  // page, then swapping to the website once bootstrap returned.
  return <PublicBlankLoader />;
}

/** @deprecated Use CreatorPublicLayout — kept for any stray imports. */
export function CreatorPublicHomeRoute() {
  return <CreatorPublicLayout />;
}

/** @deprecated Use CreatorPublicLayout — kept for any stray imports. */
export function CreatorPublicNestedRoute() {
  return <CreatorPublicLayout />;
}

/** LiB index child — only rendered when layout surface is link-in-bio. */
export function CreatorPublicLibHome() {
  return (
    <Suspense fallback={<PublicBlankLoader />}>
      <FollowerViewNew />
    </Suspense>
  );
}

/** LiB nested child — creator pages under `/{username}/:pageSlug`. */
export function CreatorPublicLibNested() {
  return (
    <Suspense fallback={null}>
      <CreatorPagesView />
    </Suspense>
  );
}
